import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import api from '../services/api';
import { useMemo, useState } from 'react';
import keyBy from 'lodash/keyBy';
import { Image, PictureAsPdf } from '@mui/icons-material';
import SimplePDFViewer from './SimplePDFViewer';
import ImageViewerDialog from './ImageViewerDialog';
import { produce } from 'immer';
import checkViewFileType from 'utils/handleViewFile';
import useAppStore from 'store/useAppStore';
import { v4 as uuidv4 } from 'uuid';
import FilePreviewButton from './FilePreviewButton';

const SelectedProject = ({ project, handleViewFile }) => {
  const setter = useAppStore((state) => state.setter);
  const showNotification = useAppStore((state) => state.showNotification);
  const setInitEditData = useAppStore((state) => state.setInitEditData);
  const [getKeyInfo, { isLoading }] = api.endpoints.getKeyInfo.useMutation();
  return (
    <>
      <List component="nav">
        {project.files.map((fileName) => {
          return (
            <ListItem key={`${project.project_id}:${fileName}`}>
              <ListItemIcon className="min-w-0 mr-2">
                {fileName.endsWith('.pdf') && <PictureAsPdf color="warning" />}
                {fileName.endsWith('.png') && <Image color="primary" />}
              </ListItemIcon>
              <ListItemText primary={fileName} />
              <FilePreviewButton
                projectId={project.project_id}
                fileName={fileName}
                handleViewFile={handleViewFile}
              />
            </ListItem>
          );
        })}
      </List>
      <Button
        fullWidth
        size="large"
        variant="contained"
        color="success"
        loading={isLoading}
        disabled={isLoading}
        onClick={() => {
          const projectName = useAppStore.getState().newProject.name.trim();
          if (!projectName) {
            showNotification('請輸入專案名稱', 'error');
            return;
          }
          getKeyInfo({
            user_id: useAppStore.getState().user.id,
            comp_name: useAppStore.getState().user.company,
            ori_project_id: project.project_id,
          })
            .unwrap()
            .then((data) => {
              setInitEditData(data);
              setter('selectedProject', (draft) => {
                draft.project_id = uuidv4();
                draft.project_name = projectName;
                draft.ori_project_id = project.project_id;
                draft.files = project.files;
                draft.full_script_id = uuidv4();
              });
            })
            .catch((error) => {
              showNotification(error.message || '發生錯誤', 'error');
            });
        }}
      >
        選擇此資料庫專案
      </Button>
    </>
  );
};

const ProjectList = () => {
  const clearSelectedProject = useAppStore(
    (state) => state.clearSelectedProject,
  );
  const userid = useAppStore((state) => state.user.id);
  const company = useAppStore((state) => state.user.company);
  const { currentData, isFetching, isError, refetch } =
    api.endpoints.getProjectList.useQuery({
      user_id: userid,
      comp_name: company,
    });
  const [selectedPreviewProjectId, setSelectedPreviewProjectId] = useState('');
  const handleChange = (event) => {
    setSelectedPreviewProjectId(event.target.value);
    clearSelectedProject(); // 切換時要重置已選的專案
  };
  const [fileViewer, setFileViewer] = useState({
    open: false,
    file: null,
    type: '',
  });
  const handleViewFile = (blob) => {
    const type = blob.type;
    const checkType = checkViewFileType(type);
    if (checkType === false) {
      return;
    }
    setFileViewer({
      type: checkType,
      open: true,
      file: window.URL.createObjectURL(blob),
    });
  };
  const projectKeyById = useMemo(() => {
    if (!currentData?.project_list) {
      return {};
    }
    return keyBy(currentData.project_list, 'project_id');
  }, [currentData?.project_list]);

  if (!currentData && isFetching) {
    return <CircularProgress />;
  }
  if (isError) {
    return (
      <div className="p-2 flex flex-col items-center">
        取得資料發生錯誤
        <Button component="span" onClick={refetch}>
          重試
        </Button>
      </div>
    );
  }
  if (currentData?.project_list.length <= 0) {
    return <div className="text-center pt-6">尚無資料</div>;
  }

  return (
    <div className="mt-6">
      <FormControl fullWidth>
        <InputLabel>資料夾</InputLabel>
        <Select
          value={selectedPreviewProjectId}
          label="資料夾"
          onChange={handleChange}
        >
          {currentData.project_list.map(({ project_id, project_name }) => {
            return (
              <MenuItem key={project_id} value={project_id}>
                {project_name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {projectKeyById[selectedPreviewProjectId] && (
        <>
          <SelectedProject
            handleViewFile={handleViewFile}
            project={projectKeyById[selectedPreviewProjectId]}
            selectedPreviewProjectId={selectedPreviewProjectId}
          />
        </>
      )}
      <SimplePDFViewer
        open={fileViewer.type === 'pdf' && fileViewer.open}
        file={fileViewer.file}
        onClose={() =>
          setFileViewer(
            produce((draft) => {
              draft.open = false;
            }),
          )
        }
      />
      <ImageViewerDialog
        open={fileViewer.type === 'image' && fileViewer.open}
        file={fileViewer.file}
        onClose={() =>
          setFileViewer(
            produce((draft) => {
              draft.open = false;
            }),
          )
        }
      />
    </div>
  );
};
export default ProjectList;
